import type * as React from 'react'
import { useState } from 'react'
import type { JSX } from 'react'

import { useMutation } from '@apollo/client/react'
import type { ApolloCache } from '@apollo/client'

import {
  AlertDialog,
  Button,
  Card,
  ChannelAvatar,
  Flex,
  Form,
  Input,
  Text,
  toast,
  Tooltip,
} from '@buffer-mono/popcorn'

import { logError } from '~publish/legacy/utils/logError'

import { RemoveChannelsMutation } from './ChannelDropdownItems'
import { isStartPage } from '../../shared/utils/isStartPage'
import { isChannelIconService } from '~publish/pages/Community/util/isChannelIconService'
import type { ChannelMask } from '../hooks/useChannelsForSettings'

import style from './DisconnectChannelAlertDialog.module.css'
import inlineTextControls from '../../shared/inlineTextControls.module.css'

export const updateCacheAfterChannelRemoval = (
  cache: ApolloCache,
  channelToRemove: Pick<ChannelMask, 'id'>,
): void => {
  // Evict the channel entity so it is dropped from every query that
  // references it (Settings, Sidebar, Composer, …). Rewriting each query by
  // hand is fragile — the cache-first Composer list kept showing a
  // disconnected channel until a manual page refresh because it was missed.
  cache.evict({
    id: cache.identify({ __typename: 'Channel', id: channelToRemove.id }),
  })
  cache.gc()
}

const DisconnectChannelAlertDialog = ({
  channel,
  children,
  handleRefreshChannelConnection,
}: {
  channel: ChannelMask
  children: React.ReactNode
  handleRefreshChannelConnection: (cta: string) => void
}): JSX.Element => {
  const [inputValue, setInputValue] = useState('')
  const [hasInputError, setHasInputError] = useState(false)
  const [executeRemoveChannels, { loading }] = useMutation(
    RemoveChannelsMutation,
  )

  const isFacebookGroup =
    channel.service === 'facebook' && channel.type === 'group'
  const isInstagramPersonalProfile =
    channel.service === 'instagram' && channel.type === 'profile'
  const isChannelStartPage = isStartPage({ channel })
  const channelType = isChannelIconService(channel.service)
    ? channel.service
    : null
  const shouldShowRefreshConnection =
    !isFacebookGroup && !isInstagramPersonalProfile && !isChannelStartPage

  const successMessage = isChannelStartPage
    ? 'Channel deleted successfully!'
    : 'Channel disconnected successfully!'

  const errorMessage = isChannelStartPage
    ? 'Failed to delete channel'
    : 'Failed to disconnect channel'

  const disconnectConfirmationText = 'disconnect'
  const isDisconnectApproved =
    inputValue.trim().toLowerCase() === disconnectConfirmationText.toLowerCase()

  const shouldShowInputError = hasInputError && !isDisconnectApproved

  const handleConfirmDisconnect = async (): Promise<void> => {
    try {
      const { data, error } = await executeRemoveChannels({
        variables: {
          channelIds: [channel.id],
        },
        update: (cache) => updateCacheAfterChannelRemoval(cache, channel),
      })

      if (data?.channelsRemove) {
        toast.success(successMessage)
        window?.appshell?.actions?.refetchAccount?.()
      }

      if (error) {
        toast.error(errorMessage)
        const unexpectedError = new Error(
          error.message || 'Unexpected API response',
        )
        logError(unexpectedError, { metaData: { data, error } })
      }
    } catch (e) {
      toast.error(errorMessage)
      logError(e instanceof Error ? e : new Error(String(e)), {
        metaData: { variables: { channel } },
      })
    }
  }

  return (
    <AlertDialog>
      <Form>
        <AlertDialog.Trigger>{children}</AlertDialog.Trigger>
        <AlertDialog.Content>
          <AlertDialog.Title>
            {isChannelStartPage
              ? 'Delete Start Page'
              : `Disconnect ${channel.name}`}
          </AlertDialog.Title>
          <AlertDialog.Description className={style.body}>
            <Flex as="section">
              <Card
                padding="small"
                className={style.channelCard}
                translate="no"
              >
                {channelType && (
                  <ChannelAvatar
                    src={channel.avatar}
                    alt={channel.displayName || channel.name}
                    channel={channelType}
                    size="small"
                  />
                )}
                <Flex direction="column">
                  <Text weight="bold">{channel.name}</Text>
                  {/* Both branches are Buffer/platform vocabulary rather than
                      channel identity, so they opt back into translation. */}
                  <Text color="subtle" translate="yes">
                    {isChannelStartPage ? 'Start Page' : channel.descriptor}
                  </Text>
                </Flex>
              </Card>
            </Flex>
            {isChannelStartPage ? (
              <Text>
                Your Start Page will be immediately deleted and your data will
                be lost. Visitors will no longer be able to access your page
                using the link.
              </Text>
            ) : (
              <>
                <Text>
                  You will no longer be able to post to this account. All posts,
                  analytics, tags, and data linked to it will be permanently
                  deleted. <strong>This cannot be undone.</strong>
                </Text>

                {shouldShowRefreshConnection && (
                  <Text>
                    Having issues with your connection? Try{' '}
                    <Button
                      type="button"
                      variant="tertiary"
                      size="small"
                      className={inlineTextControls.inlineButton}
                      onClick={(): void => {
                        handleRefreshChannelConnection(
                          `disconnect-channel-refresh-connection-${channel.service.toLowerCase()}-1`,
                        )
                      }}
                    >
                      Refresh Connection
                    </Button>{' '}
                    first.
                  </Text>
                )}
              </>
            )}

            <Flex as="section" direction="column" gap="2xs">
              <Form.Field name="channelName" className={style.formField}>
                <Form.Label className={style.formLabel}>
                  Type &quot;{disconnectConfirmationText}&quot; to confirm.
                </Form.Label>
                <Form.Control>
                  <Input
                    className={style.fullWidth}
                    value={inputValue}
                    onChange={(e): void => setInputValue(e.target.value)}
                    placeholder={disconnectConfirmationText}
                    aria-invalid={shouldShowInputError}
                    aria-required
                    aria-describedby={
                      shouldShowInputError ? 'channelName-error' : undefined
                    }
                  />
                </Form.Control>
              </Form.Field>
              {shouldShowInputError && (
                <Text
                  id="channelName-error"
                  as="span"
                  color="critical"
                  size="sm"
                  aria-live="polite"
                  role="alert"
                >
                  Please type &quot;{disconnectConfirmationText}&quot; to
                  confirm this action.
                </Text>
              )}
            </Flex>
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel>
              <Button
                variant="tertiary"
                onClick={(): void => {
                  setInputValue('')
                  setHasInputError(false)
                }}
              >
                Cancel
              </Button>
            </AlertDialog.Cancel>
            {!isDisconnectApproved ? (
              <Tooltip
                content={`Please enter "${disconnectConfirmationText}" to confirm.`}
              >
                <Button
                  variant="critical"
                  loading={loading}
                  disabled={inputValue.length === 0}
                  onClick={(): void => {
                    setHasInputError(true)
                  }}
                >
                  {isChannelStartPage
                    ? 'Confirm and Delete'
                    : 'Disconnect Channel'}
                </Button>
              </Tooltip>
            ) : (
              <Button
                variant="critical"
                loading={loading}
                disabled={loading}
                onClick={(): void => {
                  void handleConfirmDisconnect()
                }}
              >
                {isChannelStartPage
                  ? 'Confirm and Delete'
                  : 'Disconnect Channel'}
              </Button>
            )}
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </Form>
    </AlertDialog>
  )
}

export default DisconnectChannelAlertDialog
